import { zodResolver } from "@hookform/resolvers/zod";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { childDefaultValues, ChildSchema } from "../types/zodAppTypes";
import { z } from "zod";
import {
  Control,
  FieldErrors,
  UseFormHandleSubmit,
  UseFormRegister,
  useForm,
  UseFormWatch,
  UseFormGetValues,
  UseFormSetValue,
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayPrepend,
  UseFieldArrayRemove,
  UseFieldArrayUpdate,
  UseFieldArrayReplace,
  useFieldArray,
} from "react-hook-form";
import useCurrentChild from "../api/useCurrentChild";
import { mergeLeadChildrenWithApi } from "../utils/mergeLeadChildren";
import { useSyncronizer } from "@/hooks/useSyncronizer";
import { NoteSchema } from "../types/zodTypes";
import { useChildMutations } from "../api/useChildMutations";
import { useChilds } from "../api/useChilds";
import usePipeline from "@/features/stage/api/usePipeline";
import useBranches from "@/features/users/api/useBranches";
import { buildEntityRejectionResolver } from "@/features/leads/utils/entityRejection";
import { formatDate } from "@/utils/date";
import { normalizeCustomInputScopesForForm } from "@/utils/getFromCustom";
import { useAppSelector } from "@/stores/hook";
import { toast } from "sonner";

/* ---------------- TYPES ---------------- */

type ChildFormData = z.infer<typeof ChildSchema>;
type NoteData = z.infer<typeof NoteSchema>;

type FormChildContextType = {
  register: UseFormRegister<ChildFormData>;
  control: Control<ChildFormData>;
  errors: FieldErrors<ChildFormData>;
  handleSubmit: UseFormHandleSubmit<ChildFormData>;
  watch: UseFormWatch<ChildFormData>;
  getValues: UseFormGetValues<ChildFormData>;
  setValue: UseFormSetValue<ChildFormData>;
  reset: (values?: Partial<ChildFormData>) => void;
  onSubmit: (data: ChildFormData) => void;
  isSubmitting: boolean;
  currentChildId: string | null | number;
  child: any;
  allChildren: any[];
  allChildrenLoading: boolean;
  currentChildsOnSelectedStage: any[];
  childsLoading: boolean;
  handleChildChange: any;
  /** The stage the drawer was opened FOR (a stage route, or the main list's
   *  single Stage chip) — null on a plain open. Readers that pick a default
   *  deal of their own (the Verified view) yield to it. */
  routeStageId: number | null;

  noteOperations: {
    fields: FieldArrayWithId<ChildFormData, "notes", "id">[];
    append: UseFieldArrayAppend<ChildFormData, "notes">;
    prepend: UseFieldArrayPrepend<ChildFormData, "notes">;
    remove: UseFieldArrayRemove;
    update: UseFieldArrayUpdate<ChildFormData, "notes">;
  };

  followUpOperations: {
    fields: FieldArrayWithId<ChildFormData, "followUps", "id">[];
    append: UseFieldArrayAppend<ChildFormData, "followUps">;
    prepend: UseFieldArrayPrepend<ChildFormData, "followUps">;
    remove: UseFieldArrayRemove;
    update: UseFieldArrayUpdate<ChildFormData, "followUps">;
    replace: UseFieldArrayReplace<ChildFormData, "followUps">;
  };

  deadlineOperations: {
    fields: FieldArrayWithId<ChildFormData, "deadlines", "id">[];
    append: UseFieldArrayAppend<ChildFormData, "deadlines">;
    prepend: UseFieldArrayPrepend<ChildFormData, "deadlines">;
    remove: UseFieldArrayRemove;
    update: UseFieldArrayUpdate<ChildFormData, "deadlines">;
    replace: UseFieldArrayReplace<ChildFormData, "deadlines">;
  };
};

/* ---------------- CONTEXT ---------------- */

const FormChildContext = createContext<FormChildContextType | null>(null);

/* ---------------- PROVIDER ---------------- */

type ChildFormProviderProps = {
  children: ReactNode;
  parentProps: any;
};

export function ChildFormProvider({
  children,
  parentProps,
}: ChildFormProviderProps) {
  const lead = parentProps?.lead;
  // When the drawer is opened from a dynamic STAGE route, this is that route's
  // stageId — used below to default-select the deal sitting in that stage
  // instead of the lead's furthest auto-selected child. Null for the main
  // Leads drawer (preserves the existing furthest-deal default).
  const routeStageId = parentProps?.routeStageId ?? null;
  // The list's Status / Sub-status chips beside the stage (main Leads list
  // filtered "Stage: Visa · Status: Visa Rejected"): the deal picked below is
  // the one carrying EVERY chip, falling back to any deal in the stage.
  const routeStatusId = parentProps?.routeStatusId ?? null;
  const routeSubStatusId = parentProps?.routeSubStatusId ?? null;
  // Default-select THIS deal on open — set by the dashboard follow-up rail
  // (the deal whose slot put the lead in the viewer's bucket), so the drawer
  // lands on the deal they're meant to follow up, never a "random" one.
  // Wins over routeStageId; both apply once per lead via the shared ref.
  const defaultChildId = parentProps?.defaultChildId ?? null;

  const childrenList = lead?.child ?? [];
  const [currentChildId, setCurrentChildId] = useState<string | null | number>(
    childrenList[0]?.id ?? null
  );

  const { stageId } = useAppSelector((state) => state.childFunctional);

  const leadId = lead?.id;

  const { childs: childsFromApi, isLoading: allChildrenLoading } = useChilds(
    leadId ? { leadId } : undefined
  );

  // Row type follows the lead payload's own `child[]` (untyped upstream), so
  // every downstream consumer keeps the shape it always had.
  const allChildren = useMemo(
    () =>
      mergeLeadChildrenWithApi<(typeof childrenList)[number]>(
        childsFromApi,
        childrenList,
      ),
    [childsFromApi, childrenList]
  );

  // Stage-filtered deals are a SUBSET of `allChildren` (every child carries
  // `stageId`), so derive them in-memory instead of firing a SECOND `/child`
  // network call — which previously re-fetched on every drawer open AND every
  // stage switch. Same data, one fewer round-trip, no UX change.
  const currentChildsOnSelectedStage = useMemo(
    () =>
      stageId
        ? allChildren.filter((c: any) => Number(c?.stageId) === Number(stageId))
        : allChildren,
    [allChildren, stageId],
  );
  const childsLoading = allChildrenLoading;

  const prevChildrenRef = useRef<any[]>(childrenList);

  // Stable id-signature so the effect doesn't re-run on every parent render
  // when `allChildren` is referentially fresh but content-equal.
  const allChildrenSignature = useMemo(
    () => allChildren.map((c: any) => c.id).join(","),
    [allChildren]
  );

  // Track which child ids we've seen across renders so a freshly-created
  // child (id wasn't in the previous set) can be auto-selected. Without this
  // the operator clicks "Add Application", the new deal lands in the list,
  // but the selection stays on whatever was active — they don't see the
  // thing they just created until they manually click it.
  const prevChildIdsRef = useRef<Set<number> | null>(null);

  // Declared BEFORE the seen-ids effect below, which reads it: true once an
  // EXPLICIT default selection (the rail's deal, or a stage route's deal)
  // has been applied for this lead — from then on automatic reselection
  // must keep its hands off the drawer.
  const routeDefaultAppliedRef = useRef(false);

  // NEVER LAND ON A REJECTED DEAL BY DEFAULT (user directive 2026-09-01,
  // Preeja Vimal: a lead-grain rail click — no explicit deal — auto-selected
  // the newest deal, which sat in a rejection status, so the drawer opened
  // on "Not Interested" while the live work was elsewhere). Every AUTOMATIC
  // pick below prefers a non-rejected deal; a fully-rejected deal list falls
  // back to list order, and EXPLICIT picks (rail childId, stage route,
  // manual chip clicks) are untouched. Pipeline resolved with the same
  // branch rule as useLeadPipeline — which can't be imported here: it reads
  // FormLeadContext, and that module imports this provider back.
  const { hasForkedBranch } = useBranches();
  const { pipeline } = usePipeline(
    undefined,
    hasForkedBranch ? (lead?.branchId ?? undefined) : undefined,
  );
  const isDealRejected = useMemo(
    () => buildEntityRejectionResolver(pipeline),
    [pipeline],
  );
  const pickDefaultChildId = (list: any[]): number | null => {
    const live = list.find((c: any) => c?.id != null && !isDealRejected(c));
    return live?.id ?? list[0]?.id ?? null;
  };

  useEffect(() => {
    prevChildrenRef.current = childrenList;

    const currentIdSet = new Set<number>(
      allChildren
        .map((c: any) => Number(c?.id))
        .filter((id: number) => Number.isFinite(id) && id > 0),
    );

    if (currentIdSet.size === 0) {
      setCurrentChildId(null);
      prevChildIdsRef.current = currentIdSet;
      return;
    }

    // First population after mount/lead-switch — initialise the seen set
    // without treating the existing children as "newly created".
    if (prevChildIdsRef.current === null) {
      prevChildIdsRef.current = currentIdSet;
      const currentValid =
        !!currentChildId && currentIdSet.has(Number(currentChildId));
      // Re-pick even a VALID selection when it's a rejected deal and a live
      // one exists: the useState initializer grabbed child[0] before the
      // rejection resolver could weigh in.
      const current = currentValid
        ? allChildren.find(
            (c: any) => Number(c?.id) === Number(currentChildId),
          )
        : null;
      if (
        !currentValid ||
        (current &&
          isDealRejected(current) &&
          allChildren.some((c: any) => !isDealRejected(c)))
      ) {
        setCurrentChildId(pickDefaultChildId(allChildren));
      }
      return;
    }

    // Diff against last seen — anything in `currentIdSet` but not in the
    // previous snapshot must have been added since last render (typically
    // the response from `createChild` invalidating the query).
    const newlyAddedIds: number[] = [];
    for (const id of currentIdSet) {
      if (!prevChildIdsRef.current.has(id)) newlyAddedIds.push(id);
    }
    prevChildIdsRef.current = currentIdSet;

    if (newlyAddedIds.length > 0) {
      // Auto-select the latest new child (highest id — auto-incremented PK
      // means most recently inserted). Covers single-create and the rare
      // batch case identically.
      //
      // …but NEVER while an explicit default (rail deal / stage route)
      // governs this drawer. `allChildren` merges TWO sources — the
      // scope-filtered /child list and the lead payload's own children —
      // and they arrive in WAVES: the second wave's "new" ids are existing
      // deals, not a fresh creation. Math.max() here stole the selection
      // from the rail's deal and landed the drawer on a rejected Qualify
      // deal instead of the due Offer Letter one (Shakir, 2026-09-01).
      // Trade-off accepted: a deal created inside a rail-opened drawer is
      // no longer auto-focused (the leads-page drawer, where creation
      // actually happens, has no explicit default and keeps the behaviour).
      if (!routeDefaultAppliedRef.current) {
        // Prefer the newest NON-REJECTED addition — a late data wave can
        // "add" existing rejected deals, and a genuinely created deal is
        // never born rejected.
        const fresh = allChildren.filter((c: any) =>
          newlyAddedIds.includes(Number(c?.id)),
        );
        const live = fresh.filter((c: any) => !isDealRejected(c));
        const pool = (live.length > 0 ? live : fresh).map((c: any) =>
          Number(c.id),
        );
        if (pool.length > 0) setCurrentChildId(Math.max(...pool));
      }
      return;
    }

    // Existing fallback: if the currently selected child vanished (deleted
    // or filtered out), drop back to the first available LIVE one. NOT when
    // an explicit default governs this drawer: the LEAD-VIEW sentinel
    // deliberately holds `currentChildId` at null (the lead's own slot is
    // the responsibility) — auto-picking a deal here would undo it.
    if (!currentChildId) {
      if (!routeDefaultAppliedRef.current) {
        setCurrentChildId(pickDefaultChildId(allChildren));
      }
      return;
    }
    if (!currentIdSet.has(Number(currentChildId))) {
      setCurrentChildId(pickDefaultChildId(allChildren));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allChildrenSignature, currentChildId]);

  // Stage-route default-selection. When the drawer opens from a stage route,
  // select the deal in THAT stage (e.g. the Qualify deal on the Qualify route)
  // instead of the lead's furthest auto-selected child. Waits for that stage's
  // child to appear in `allChildren` (it arrives via the useChilds query),
  // applies exactly once per lead, then yields to manual deal switches. Fully
  // gated on `routeStageId` so the main Leads drawer is untouched.
  useEffect(() => {
    routeDefaultAppliedRef.current = false;
  }, [leadId]);
  // Explicit deal default (follow-up rail). Declared before the stage-route
  // effect and marks the shared ref, so an explicit deal always wins and
  // manual deal switches afterwards are untouched.
  //
  // KEY-LATCHED, not once-per-lead: the drawer is kept alive through its
  // close animation, so a quick re-open of the same lead reuses this mounted
  // provider. The rail nulls `defaultChildId` on close — that resets the
  // latch — and the next click's (lead, deal) key applies again, landing the
  // drawer on the clicked follow-up's deal every time instead of whatever
  // was selected before.
  const appliedDefaultKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (defaultChildId == null) {
      appliedDefaultKeyRef.current = null;
      return;
    }
    const key = `${leadId ?? ""}:${defaultChildId}`;
    if (appliedDefaultKeyRef.current === key) return;
    // LEAD-GRAIN SENTINEL (-1): the clicked rail row's due slot is the
    // LEAD'S OWN — no specific deal carries the follow-up (Preeja Vimal,
    // 2026-09-01: the naive auto-pick landed on a REJECTED deal). Select
    // the lead's REPRESENTATIVE live deal — the one mirroring the lead's
    // own position (same stage + status, then same stage, then any live
    // deal), which is where the work the rail displayed actually happens.
    // Only a lead whose deals are ALL rejected (or has none) opens on the
    // LEAD view. Waits for the deal list to settle before giving up.
    if (Number(defaultChildId) === -1) {
      const leadStageId = Number(lead?.stageId ?? lead?.stage?.id);
      const leadStatusId = Number(lead?.statusId ?? lead?.status?.id);
      const live = allChildren.filter(
        (c: any) => c?.id != null && !isDealRejected(c),
      );
      const rep =
        live.find(
          (c: any) =>
            Number(c?.stageId) === leadStageId &&
            Number(c?.statusId) === leadStatusId,
        ) ??
        live.find((c: any) => Number(c?.stageId) === leadStageId) ??
        live[0] ??
        null;
      if (rep?.id != null) {
        appliedDefaultKeyRef.current = key;
        routeDefaultAppliedRef.current = true;
        setCurrentChildId(rep.id);
      } else if (!childsLoading) {
        appliedDefaultKeyRef.current = key;
        routeDefaultAppliedRef.current = true;
        setCurrentChildId(null);
      }
      return;
    }
    const target = allChildren.find(
      (c: any) => Number(c?.id) === Number(defaultChildId),
    );
    if (target?.id != null) {
      appliedDefaultKeyRef.current = key;
      routeDefaultAppliedRef.current = true;
      setCurrentChildId(target.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultChildId, allChildrenSignature, leadId]);
  useEffect(() => {
    if (routeStageId == null) return;
    // An explicit deal default is still waiting for its child to load —
    // don't let the stage-route fallback claim the once-per-lead slot.
    if (defaultChildId != null) return;
    if (routeDefaultAppliedRef.current) return;
    const inStage = allChildren.filter(
      (c: any) => Number(c?.stageId) === Number(routeStageId),
    );
    // Most specific first: the deal carrying stage + status + sub-status
    // (the list's exact chips — a rejected deal included, since that is the
    // deal the filter matched), then stage + status, then any deal in the
    // stage. A lead listed on its OWN row only (no deal carries the chips)
    // keeps the plain stage pick.
    const statusOf = (c: any) => Number(c?.statusId ?? c?.status?.id);
    const subStatusOf = (c: any) => Number(c?.subStatusId ?? c?.subStatus?.id);
    const routeStageChild =
      (routeStatusId != null && routeSubStatusId != null
        ? inStage.find(
            (c: any) =>
              statusOf(c) === Number(routeStatusId) &&
              subStatusOf(c) === Number(routeSubStatusId),
          )
        : undefined) ??
      (routeStatusId != null
        ? inStage.find((c: any) => statusOf(c) === Number(routeStatusId))
        : undefined) ??
      inStage[0];
    if (routeStageChild?.id != null) {
      routeDefaultAppliedRef.current = true;
      setCurrentChildId(routeStageChild.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeStageId, routeStatusId, routeSubStatusId, defaultChildId, allChildrenSignature, leadId]);

  const { child, refetch } = useCurrentChild(currentChildId);

  // Refetch child when ID changes
  useSyncronizer(currentChildId, () => {
    if (currentChildId) refetch();
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<ChildFormData>({
    resolver: zodResolver(ChildSchema),
    defaultValues: childDefaultValues,
    mode: "onChange",
  });
  const {
    fields: noteFields,
    append: noteAppend,
    prepend: notePrepend,
    remove: noteRemove,
    update: noteUpdate,
  } = useFieldArray({
    control,
    name: "notes",
  });

  const {
    fields: followUpFields,
    append: followUpAppend,
    prepend: followUpPrepend,
    remove: followUpRemove,
    update: followUpUpdate,
    replace: followUpReplace,
  } = useFieldArray({
    control,
    name: "followUps",
  });

  const {
    fields: deadlineFields,
    append: deadlineAppend,
    prepend: deadlinePrepend,
    remove: deadlineRemove,
    update: deadlineUpdate,
    replace: deadlineReplace,
  } = useFieldArray({
    control,
    name: "deadlines",
  });

  const { updateChild } = useChildMutations();

  const handleChildChange = useCallback(
    (id: string | number) => {
      setCurrentChildId(id);
      refetch();
    },
    [refetch]
  );

  const onSubmit = useCallback(
    (data: ChildFormData) => {
      if (!currentChildId) return;
      updateChild.mutate({ id: currentChildId, data });
    },
    [currentChildId, updateChild]
  );

  const onInvalid = (errs: FieldErrors<ChildFormData>) => {
    const keys = Object.keys(errs) as Array<keyof ChildFormData>;
    if (keys.length > 0) {
      const firstError = (errs as any)[keys[0]]?.message;
      toast.error(firstError || "Please check the child form for errors");
    }
  };

  const wrappedHandleSubmit = useCallback<UseFormHandleSubmit<ChildFormData>>(
    (fn) => handleSubmit(fn, onInvalid),
    [handleSubmit]
  );

  const noteOperations = useMemo(
    () => ({
      fields: noteFields,
      append: noteAppend,
      prepend: notePrepend,
      remove: noteRemove,
      update: noteUpdate,
    }),
    [noteFields, noteAppend, notePrepend, noteRemove, noteUpdate]
  );

  const followUpOperations = useMemo(
    () => ({
      fields: followUpFields,
      append: followUpAppend,
      prepend: followUpPrepend,
      remove: followUpRemove,
      update: followUpUpdate,
      replace: followUpReplace,
    }),
    [
      followUpFields,
      followUpAppend,
      followUpPrepend,
      followUpRemove,
      followUpUpdate,
      followUpReplace,
    ]
  );

  const deadlineOperations = useMemo(
    () => ({
      fields: deadlineFields,
      append: deadlineAppend,
      prepend: deadlinePrepend,
      remove: deadlineRemove,
      update: deadlineUpdate,
      replace: deadlineReplace,
    }),
    [
      deadlineFields,
      deadlineAppend,
      deadlinePrepend,
      deadlineRemove,
      deadlineUpdate,
      deadlineReplace,
    ]
  );

  const isSubmitting = updateChild.isPending;

  const value = useMemo<FormChildContextType>(
    () => ({
      register,
      control,
      errors,
      handleSubmit: wrappedHandleSubmit,
      watch,
      getValues,
      setValue,
      reset,
      onSubmit,
      isSubmitting,
      currentChildId,
      child,
      allChildren,
      allChildrenLoading,
      currentChildsOnSelectedStage,
      childsLoading,
      handleChildChange,
      routeStageId,
      noteOperations,
      followUpOperations,
      deadlineOperations,
    }),
    [
      register,
      control,
      errors,
      wrappedHandleSubmit,
      watch,
      getValues,
      setValue,
      reset,
      onSubmit,
      isSubmitting,
      currentChildId,
      child,
      allChildren,
      allChildrenLoading,
      currentChildsOnSelectedStage,
      childsLoading,
      handleChildChange,
      routeStageId,
      noteOperations,
      followUpOperations,
      deadlineOperations,
    ]
  );

  /**
   * Sync RHF state when:
   *   (a) the active child changes (user picks a different deal), OR
   *   (b) the same child's server data refetches in place (e.g. AttachCourseModal
   *       patches `customInputScopes` via useAttachVariant, which refetches but
   *       does not run reset() itself).
   *
   * Deps:
   *   - `child` — covers (b). React Query produces a new object ref when the
   *     response differs (structuralSharing keeps the ref stable on deep-equal
   *     refetches, which is what we want — no churn when nothing changed).
   *   - `currentChildId` — covers (a). Necessary because RQ's structuralSharing
   *     returns the *same* cached object reference when the user revisits a
   *     deal they already loaded (A → B → A). Without this dep, the previous
   *     memo-based sync key never recomputed on revisit and `reset()` silently
   *     skipped, leaving the Preference panel stuck on the previously-active
   *     deal's CF values. This was the "sometimes the country switches,
   *     sometimes it doesn't" bug — it only manifested on cached re-visits.
   *
   * Guard: skip if `child.id !== currentChildId` so a late-arriving stale fetch
   * (e.g. user clicks deal B, then deal C before B resolves) can't overwrite
   * the form with the wrong child's data.
   */
  useEffect(() => {
    if (!(child?.id && parentProps?.lead?.id)) return;
    if (String(child.id) !== String(currentChildId)) return;

    reset({
      name: child?.name ?? "",
      // Mirror the lead form (FormLeadContext): the scalar holds the deal's
      // PENDING follow-up date, else empty. Never default to today —
      // `new Date()` here fabricated a phantom "follow-up today" in the footer
      // chip (ActionForNextFollowup falls back to this slot when the deal has
      // no follow-up rows), disagreeing with the Followups tab. `""` keeps the
      // required-string schema happy.
      followUpDate:
        child?.followUpDates?.find((val: any) => val.status === "Pending")
          ?.date ?? "",
      followUps:
        child?.followUpDates?.map((val: any) => {
          return {
            id: val.id,
            createdBy: val.createdBy,
            date: val.date,
            isSystem: val.isSystem,
            createdAt: val.createdAt,
            status: val.status,
            // THE SLOT (role-based follow-ups). This mapper hand-picks
            // fields, and dropping these here starved every role UI
            // downstream — keep them forever.
            roleId: val.roleId ?? null,
            role: val.role ?? null,
            note: val?.note
              ? {
                  note: val.note?.content,
                  createdAt: val.note.createdAt,
                  createdByName: val.note.createdBy.name,
                }
              : {},
            completedBy: val.completedBy,
          };
        }) ?? [],
      deadlines:
        (child?.deadlineDates ?? []).map((val: any) => ({
          id: val.id,
          createdBy: val.createdBy,
          date: val.date,
          isSystem: val.isSystem,
          createdAt: val.createdAt,
          status: val.status,
          fromStatusId: val.fromStatusId,
          toStatusId: val.toStatusId,
          fromSubStatusId: val.fromSubStatusId,
          toSubStatusId: val.toSubStatusId,
          fromStatus: val.fromStatus
            ? { ...val.fromStatus, stageId: val.fromStatus.stageId }
            : val.fromStatus,
          toStatus: val.toStatus
            ? { ...val.toStatus, stageId: val.toStatus.stageId }
            : val.toStatus,
          fromSubStatus: val.fromSubStatus,
          toSubStatus: val.toSubStatus,
          note: val?.note
            ? {
                note: val.note?.content,
                createdAt: val.note.createdAt,
                createdByName: val.note.createdBy.name,
              }
            : {},
          completedBy: val.completedBy,
          warningDays: val.warningDays ?? null,
          triggeredAt: val.triggeredAt ?? null,
          updatedAt: val.updatedAt ?? null,
        })) ?? [],
      notes:
        child?.notes?.map((val: any) => ({
          note: val.content,
          createdById: String(val.createdBy.id),
          createdByName: val.createdBy.name,
          // "Switch user" byline — see authorByline.ts. Null on ordinary notes.
          switchedByName: val.switchedByUser?.name ?? null,
          createdAt: formatDate(val.createdAt),
          noteId: val.id,
          isDeleted: Boolean(val.isDeleted),
          editedAt: val.editedAt ?? null,
        })) ?? [],
      customInputScopes: normalizeCustomInputScopesForForm(
        child?.customInputScopes,
      ),
      documents: child?.documents ?? [],
    });
  }, [child, currentChildId, parentProps?.lead?.id, reset]);

  return (
    <FormChildContext.Provider value={value}>
      {children}
    </FormChildContext.Provider>
  );
}

function useChildNotes() {
  const {
    noteOperations,
    child,
    currentChildId: selectedChildId,
    getValues,
  } = useChildForm();
  const { patchChildSilently } = useChildMutations();
  // The SELECTED deal is the one the composer belongs to; `child` is its
  // fetched payload and lands a beat later. `useActiveDataMutation` routes a
  // note here on the selection alone, so keying the save on `child?.id` meant
  // every note typed inside that window was prepended and never sent.
  const currentChildId = child?.id ?? selectedChildId;

  // Same contract as `useLeadNotes.addNote`: `false` = refused (nothing
  // sent), `options.onFailed` = sent but not saved. The composer uses both to
  // keep the text, so a written comment is never simply dropped.
  const addNote = (
    note: NoteData,
    options?: { onFailed?: () => void },
  ): boolean => {
    // `switchedByName` is a CLIENT-ONLY byline for the optimistic row (see
    // authorByline.ts). The server derives the real author from the session
    // itself, so it never travels in the request.
    const { switchedByName: _byline, ...noteForServer } = note as NoteData & {
      switchedByName?: string | null;
    };

    // Deal-side twin of the lead guard — see `useLeadNotes.addNote`: never
    // paint a note we have nowhere to send.
    if (!currentChildId) {
      toast.error("This deal is still loading — please add the note again.");
      return false;
    }

    const createdAt = note.createdAt ?? new Date().toISOString();
    noteOperations.prepend({
      ...note,
      createdById: String(note.createdById),
      createdAt,
    });

    patchChildSilently.mutate(
      {
        id: currentChildId,
        data: { note: noteForServer },
      },
      {
        // An optimistic note whose save failed is taken back out of the
        // thread instead of sitting there looking saved until the next
        // refetch. Same matcher as the lead side.
        onError: () => {
          const list = (getValues("notes") as any[]) ?? [];
          const index = list.findIndex(
            (n: any) =>
              !n?.noteId && n?.createdAt === createdAt && n?.note === note.note,
          );
          if (index >= 0) noteOperations.remove(index);
          options?.onFailed?.();
        },
      },
    );
    return true;
  };

  return {
    notes: noteOperations.fields,
    addNote,
    deleteNote: (index: number) => noteOperations.remove(index),
    updateNote: (index: number, note: NoteData) =>
      noteOperations.update(index, note),
  };
}

// ---------------------------------------------------------
// FollowUps Hook (DESC order: newest on top) ✅
// ---------------------------------------------------------
function useChildFollowUps() {
  const { followUpOperations, getValues, currentChildId, setValue } =
    useChildForm();
  const { patchChildSilently } = useChildMutations();

  /**
   * @param options Optional callbacks forwarded to the silent PATCH. The
   * drawer uses them to chain work that must happen only AFTER the row is
   * really saved — creating a follow-up can also apply an unsaved Stage/status
   * pick, and the move must not be attempted on a follow-up that failed to
   * write (see ActionForNextFollowup / PendingStageDraft). Callers that pass
   * nothing behave exactly as before: fire-and-forget.
   */
  const addFollowUp = (
    followUp: any,
    options?: { onSuccess?: () => void; onError?: (error: unknown) => void },
  ) => {
    const currentList = (getValues("followUps") as any[]) ?? [];

    // ROLE-SCOPED local supersede (2026-08-30): only the SAME slot's pending
    // row (or a pre-upgrade role-less one) completes when a new follow-up is
    // added — other roles' pending rows are THEIR follow-ups and stay live,
    // mirroring the server's role-scoped supersede exactly.
    const newRoleId = followUp?.role?.id ?? followUp?.roleId ?? null;
    const completedList = currentList.map((fu) => {
      if (fu?.status === "Complete") return fu;
      const fuRoleId = fu?.role?.id ?? fu?.roleId ?? null;
      if (fuRoleId !== null && newRoleId !== null && fuRoleId !== newRoleId) {
        return fu; // another role's live slot — untouched
      }
      return { ...fu, status: "Complete" };
    });

    const newFollowUp = {
      ...followUp,
      createdAt: followUp.createdAt ?? new Date().toISOString(),
      status: "Pending",
      // ✅ keep required schema keys safe
      isSystem: Boolean(followUp?.isSystem),
      completedBy: followUp?.completedBy ?? {},
    };

    // ✅ ONE state update -> UI always refresh
    followUpOperations.replace([newFollowUp, ...completedList]);
    setValue("followUpDate", newFollowUp.date, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    if (currentChildId) {
      patchChildSilently.mutate(
        {
          type: "updateFollowup",
          id: currentChildId,
          data: { followUp: newFollowUp },
        },
        options
          ? {
              onSuccess: () => options.onSuccess?.(),
              onError: (error) => options.onError?.(error),
            }
          : undefined,
      );
    } else {
      // Nothing was written, so nothing may be chained off it.
      options?.onError?.(new Error("No deal in context"));
    }
  };

  // NOTE: no `completeFollowUp` / `deleteFollowUp`. A follow-up is a single
  // always-live date that can only be RESCHEDULED (2026-08-20) — it is never
  // completed away and never deleted. `addFollowUp` supersedes the previous
  // row server-side, which is the only way the date changes.
  return {
    followUps: followUpOperations.fields,
    addFollowUp,
    updateFollowUp: (index: number, fu: any) =>
      followUpOperations.update(index, fu),
  };
}

function useChildDeadlines() {
  const { deadlineOperations, getValues, currentChildId } = useChildForm();
  const { patchChildSilently } = useChildMutations();

  const addDeadline = (deadline: any) => {
    const currentList = (getValues("deadlines") as any[]) ?? [];

    const completedList = currentList.map((d) => {
      if (d?.status === "Complete") return d;
      return { ...d, status: "Complete" };
    });

    const newDeadline = {
      ...deadline,
      createdAt: deadline.createdAt ?? new Date().toISOString(),
      status: "Pending",
      isSystem: Boolean(deadline?.isSystem),
      completedBy: deadline?.completedBy ?? {},
    };

    deadlineOperations.replace([newDeadline, ...completedList]);

    if (currentChildId) {
      patchChildSilently.mutate({
        type: "updateDeadline",
        id: currentChildId,
        data: { deadline: newDeadline },
      });
    }
  };

  const completeDeadline = (
    index: number,
    completedUser: { id: string | number; name: string }
  ) => {
    const current = getValues(`deadlines.${index}`) as any;
    if (!current) return;

    const updated = {
      ...current,
      status: "Complete",
      completedBy: {
        ...(current.completedBy ?? {}),
        id: String(completedUser.id),
        name: completedUser.name,
      },
    };

    deadlineOperations.update(index, updated);

    if (currentChildId) {
      patchChildSilently.mutate({
        type: "completeDeadline",
        id: currentChildId,
        data: { deadline: updated },
      });
    }
  };

  return {
    deadlines: deadlineOperations.fields,
    addDeadline,
    deleteDeadline: (index: number) => deadlineOperations.remove(index),
    updateDeadline: (index: number, d: any) =>
      deadlineOperations.update(index, d),
    completeDeadline,
  };
}

/* ---------------- HOOK ---------------- */

function useChildForm(): FormChildContextType {
  const context = useContext(FormChildContext);

  if (!context) {
    throw new Error("useChildForm must be used within ChildFormProvider");
  }

  return context;
}
export { useChildNotes, useChildForm, useChildFollowUps, useChildDeadlines };
